import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../../supabase'
import { formatEuro, MOIS_LABELS } from '../../utils/calculs'
import './Rapports.css'

const CAPACITE = { SPA: 42, HYDRO: 7 }

// Fix bug UTC : construction explicite du dernier jour sans toISOString()
function finDeMois(annee, mois) {
  const dernierJour = new Date(annee, mois, 0).getDate()
  return `${annee}-${String(mois).padStart(2,'0')}-${String(dernierJour).padStart(2,'0')}`
}


async function fetchPeriode(debut, fin) {
  const [{ data: seances }, { data: accueil }, { data: financier }] = await Promise.all([
    supabase.from('seances')
      .select('date, pole, ca_ttc, remuneration_affichee, total_massage_payant, total_massage_gratuit, total_massage')
      .gte('date', debut).lte('date', fin),
    supabase.from('seances_accueil')
      .select('date, montant').gte('date', debut).lte('date', fin),
    supabase.from('saisies_financieres')
      .select('*').gte('date_debut', debut).lte('date_debut', fin)
  ])

  const spa = (seances || []).filter(s => s.pole === 'SPA')
  const hydro = (seances || []).filter(s => s.pole === 'HYDRO')

  return {
    ca_massages_spa: spa.reduce((a, s) => a + (s.ca_ttc || 0), 0),
    ca_massages_hydro: hydro.reduce((a, s) => a + (s.ca_ttc || 0), 0),
    rem_sup_sde: spa.reduce((a, s) => a + (s.remuneration_affichee || 0), 0),
    rem_sup_hydro: hydro.reduce((a, s) => a + (s.remuneration_affichee || 0), 0),
    rem_accueil: (accueil || []).reduce((a, s) => a + (s.montant || 0), 0),
    cb: (financier || []).reduce((a, s) => a + (s.cb_pin || 0) + (s.cb_sans_contact || 0), 0),
    especes: (financier || []).reduce((a, s) => a + (s.especes || 0), 0),
    cheques: (financier || []).reduce((a, s) => a + (s.cheque_comptant || 0) + (s.cheque_differe || 0), 0),
    massages_dispo_spa: CAPACITE.SPA * spa.length,
    massages_payants_spa: spa.reduce((a, s) => a + (s.total_massage_payant || 0), 0),
    massages_gratuits_spa: spa.reduce((a, s) => a + (s.total_massage_gratuit || 0), 0),
    massages_total_spa: spa.reduce((a, s) => a + (s.total_massage || 0), 0),
    massages_dispo_hydro: CAPACITE.HYDRO * hydro.length,
    massages_payants_hydro: hydro.reduce((a, s) => a + (s.total_massage_payant || 0), 0),
    massages_gratuits_hydro: hydro.reduce((a, s) => a + (s.total_massage_gratuit || 0), 0),
    massages_total_hydro: hydro.reduce((a, s) => a + (s.total_massage || 0), 0),
  }
}

function ColonneRapport({ d, titre, commentaires, onCommentaire, readOnly }) {
  if (!d) return null
  const ca_massages = d.ca_massages_spa + d.ca_massages_hydro
  const rem_total = d.rem_sup_sde + d.rem_sup_hydro + d.rem_accueil
  const ca_enc = d.cb + d.especes + d.cheques

  const rows = [
    { section: 'enc', key: 'cb', label: "VENTES CB'S", val: d.cb },
    { section: 'enc', key: 'especes', label: 'VENTES ESPÈCE', val: d.especes },
    { section: 'enc', key: 'cheques', label: 'VENTES CHÈQUES', val: d.cheques },
    { section: 'mass', key: 'spa', label: 'CA MASSAGES', val: d.ca_massages_spa },
    { section: 'mass', key: 'hydro', label: 'CA HYDRO', val: d.ca_massages_hydro },
    { section: 'rem', key: 'sde', label: 'SUP SDE', val: d.rem_sup_sde },
    { section: 'rem', key: 'hydro', label: 'SUP HYDRO', val: d.rem_sup_hydro },
    { section: 'rem', key: 'accueil', label: 'ACCUEIL', val: d.rem_accueil },
    { section: 'to', key: 'spa', label: 'SDE', val: d.massages_total_spa, nb: d.massages_dispo_spa },
    { section: 'to', key: 'hydro', label: 'HYDRO', val: d.massages_total_hydro, nb: d.massages_dispo_hydro },
  ]

  return (
    <div className="rapport-col">
      {titre && <div className="rapport-col-titre">{titre}</div>}

      {/* Encaissements */}
      <table className="rt">
        <thead><tr><th>ENCAISSEMENTS</th><th className="rt-val">MONTANT</th><th className="rt-com">COMMENTAIRES</th></tr></thead>
        <tbody>
          {rows.filter(r => r.section === 'enc').map(r => (
            <tr key={r.key}>
              <td>{r.label}</td>
              <td className="rt-val">{formatEuro(r.val)}</td>
              <td className="rt-com">
                {!readOnly
                  ? <input className="com-input" value={commentaires[`e_${r.key}`] || ''} onChange={e => onCommentaire(`e_${r.key}`, e.target.value)} />
                  : <span>{commentaires[`e_${r.key}`] || ''}</span>}
              </td>
            </tr>
          ))}
          <tr className="rt-total"><td>TOTAL</td><td className="rt-val">{formatEuro(ca_enc)}</td><td className="rt-com"></td></tr>
        </tbody>
      </table>

      {/* Massages */}
      <table className="rt">
        <thead><tr><th>CA MASSAGES TTC</th><th className="rt-val">MONTANT</th><th className="rt-com">COMMENTAIRES</th></tr></thead>
        <tbody>
          {rows.filter(r => r.section === 'mass').map(r => (
            <tr key={r.key}>
              <td>{r.label}</td>
              <td className="rt-val">{formatEuro(r.val)}</td>
              <td className="rt-com">
                {!readOnly
                  ? <input className="com-input" value={commentaires[`m_${r.key}`] || ''} onChange={e => onCommentaire(`m_${r.key}`, e.target.value)} />
                  : <span>{commentaires[`m_${r.key}`] || ''}</span>}
              </td>
            </tr>
          ))}
          <tr className="rt-total"><td>TOTAL</td><td className="rt-val">{formatEuro(ca_massages)}</td><td className="rt-com"></td></tr>
        </tbody>
      </table>

      {/* Rémunération */}
      <table className="rt">
        <thead><tr><th>RÉMUNÉRATION</th><th className="rt-val">MONTANT</th><th className="rt-com">COMMENTAIRES</th></tr></thead>
        <tbody>
          {rows.filter(r => r.section === 'rem').map(r => (
            <tr key={r.key}>
              <td>{r.label}</td>
              <td className="rt-val">{formatEuro(r.val)}</td>
              <td className="rt-com">
                {!readOnly
                  ? <input className="com-input" value={commentaires[`r_${r.key}`] || ''} onChange={e => onCommentaire(`r_${r.key}`, e.target.value)} />
                  : <span>{commentaires[`r_${r.key}`] || ''}</span>}
              </td>
            </tr>
          ))}
          <tr className="rt-total"><td>TOTAL</td><td className="rt-val">{formatEuro(rem_total)}</td><td className="rt-com"></td></tr>
        </tbody>
      </table>

      {/* TO */}
      <table className="rt">
        <thead><tr><th>T.O</th><th className="rt-val">NB.</th><th className="rt-com">COMMENTAIRES</th></tr></thead>
        <tbody>
          {rows.filter(r => r.section === 'to').map(r => (
            <tr key={r.key}>
              <td>{r.label}</td>
              <td className="rt-val">{r.val} / {r.nb}</td>
              <td className="rt-com">
                {!readOnly
                  ? <input className="com-input" value={commentaires[`t_${r.key}`] || ''} onChange={e => onCommentaire(`t_${r.key}`, e.target.value)} />
                  : <span>{commentaires[`t_${r.key}`] || ''}</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export default function Rapports() {
  const now = new Date()
  const [type, setType] = useState('hebdo')
  const [annee, setAnnee] = useState(now.getFullYear())
  const [mois, setMois] = useState(now.getMonth() + 1)
  const [dateDebut, setDateDebut] = useState('')
  const [dateFin, setDateFin] = useState('')
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [commentaires, setCommentaires] = useState({})

  useEffect(() => {
    const d = new Date()
    const day = d.getDay()
    const mon = new Date(d); mon.setDate(d.getDate() - day + (day === 0 ? -6 : 1))
    const sun = new Date(mon); sun.setDate(mon.getDate() + 6)
    setDateDebut(mon.toISOString().split('T')[0])
    setDateFin(sun.toISOString().split('T')[0])
  }, [])

  const load = useCallback(async () => {
    setLoading(true); setData(null)
    const debut = type === 'mensuel' ? `${annee}-${String(mois).padStart(2,'0')}-01` : dateDebut
    // Fix bug UTC : utilisation du helper finDeMois
    const fin = type === 'mensuel' ? finDeMois(annee, mois) : dateFin
    if (!debut || !fin) { setLoading(false); return }

    const current = await fetchPeriode(debut, fin)
    let prev1 = null, prev2 = null
    if (type === 'mensuel') {
      ;[prev1, prev2] = await Promise.all([
        fetchPeriode(`${annee-1}-${String(mois).padStart(2,'0')}-01`, finDeMois(annee-1, mois)),
        fetchPeriode(`${annee-2}-${String(mois).padStart(2,'0')}-01`, finDeMois(annee-2, mois)),
      ])
    }
    setData({ current, prev1, prev2, debut, fin })
    setCommentaires({})
    setLoading(false)
  }, [type, annee, mois, dateDebut, dateFin])

  useEffect(() => {
    if (type === 'mensuel' || (dateDebut && dateFin)) load()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load, type, dateDebut, dateFin])

  function titreRapport() {
    if (type === 'mensuel') return `C.A DU MOIS DE ${MOIS_LABELS[mois].toUpperCase()} ${annee}`
    if (dateDebut && dateFin) {
      const d = new Date(dateDebut).toLocaleDateString('fr-FR')
      const f = new Date(dateFin).toLocaleDateString('fr-FR')
      return `C.A DE LA SEMAINE DU ${d} AU ${f}`
    }
    return 'RAPPORT'
  }

  const annees = [2023, 2024, 2025, 2026, 2027]

  return (
    <div>
      <div className="no-print">
        <div className="page-header">
          <div>
            <h2>Rapports</h2>
            <div className="page-header-sub">Rapport hebdomadaire et mensuel</div>
          </div>
          <button className="btn btn-bordeaux" onClick={() => window.print()} disabled={!data}>
            ⬇ Exporter en PDF
          </button>
        </div>

        <div className="tabs" style={{marginBottom:20}}>
          <button className={`tab-btn ${type === 'hebdo' ? 'active' : ''}`} onClick={() => setType('hebdo')}>Hebdomadaire</button>
          <button className={`tab-btn ${type === 'mensuel' ? 'active' : ''}`} onClick={() => setType('mensuel')}>Mensuel</button>
        </div>

        {type === 'mensuel' ? (
          <div style={{display:'flex', gap:12, marginBottom:20}}>
            <select value={annee} onChange={e => setAnnee(+e.target.value)}
              style={{padding:'8px 12px', border:'1px solid var(--border)', borderRadius:'var(--radius)'}}>
              {annees.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
            <select value={mois} onChange={e => setMois(+e.target.value)}
              style={{padding:'8px 12px', border:'1px solid var(--border)', borderRadius:'var(--radius)'}}>
              {MOIS_LABELS.slice(1).map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
            </select>
            <button className="btn btn-outline btn-sm" onClick={load}>Actualiser</button>
          </div>
        ) : (
          <div style={{display:'flex', gap:12, marginBottom:20, alignItems:'flex-end'}}>
            <div className="form-group" style={{margin:0}}>
              <label>Du</label>
              <input type="date" value={dateDebut} onChange={e => setDateDebut(e.target.value)} />
            </div>
            <div className="form-group" style={{margin:0}}>
              <label>Au</label>
              <input type="date" value={dateFin} onChange={e => setDateFin(e.target.value)} />
            </div>
            <button className="btn btn-outline btn-sm" onClick={load}>Actualiser</button>
          </div>
        )}
      </div>

      {loading && <div className="spinner" />}

      {data && !loading && (
        <div className="rapport-print-zone">
          <div className="rapport-entete">
            <div className="rapport-entete-titre">{titreRapport()}</div>
            <div className="rapport-entete-sub">École Internationale du Spa</div>
          </div>

          {type === 'mensuel' ? (
            <div className="rapport-grille">
              <ColonneRapport d={data.current}
                titre={`RÉSUMÉ ${MOIS_LABELS[mois].toUpperCase()} ${annee}`}
                commentaires={commentaires}
                onCommentaire={(k, v) => setCommentaires(c => ({...c, [k]: v}))}
                readOnly={false} />
              <ColonneRapport d={data.prev1}
                titre={`COMPARATIF ${MOIS_LABELS[mois].toUpperCase()} ${annee - 1}`}
                commentaires={{}} onCommentaire={() => {}} readOnly={true} />
              <ColonneRapport d={data.prev2}
                titre={`COMPARATIF ${MOIS_LABELS[mois].toUpperCase()} ${annee - 2}`}
                commentaires={{}} onCommentaire={() => {}} readOnly={true} />
            </div>
          ) : (
            <div className="rapport-grille rapport-grille-1col">
              <ColonneRapport d={data.current}
                commentaires={commentaires}
                onCommentaire={(k, v) => setCommentaires(c => ({...c, [k]: v}))}
                readOnly={false} />
            </div>
          )}
        </div>
      )}
    </div>
  )
}
